import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import debounce from 'lodash.debounce'
import Modal from 'react-bootstrap/Modal'
import Tabs from 'react-bootstrap/Tabs'
import Tab from 'react-bootstrap/Tab'
import Form from 'react-bootstrap/Form'
import { selectTranslations } from '../../../services/i18n/i18nSlice'
import { searchExams } from '../../ExamScreen/ExamApi'
import { searchUsers, searchGroups } from '../../UsersScreen/UsersApi'
import close_img from '../../../assets/images/close.svg'

const TABS_CONFIG = {
    users: {
        search: (pattern) => searchUsers({ pattern }),
        getId: (u) => u.user_id,
        getLabel: (u) => `${u.email}${u.firstname || u.lastname ? ` - ${u.firstname || ''} ${u.lastname || ''}`.trimEnd() : ''}`
    },
    exams: {
        search: (pattern) => searchExams({ search_pattern: pattern }).then(r => ({ ...r, data: r.data?.exams })),
        getId: (e) => e.exam_id,
        getLabel: (e) => e.title
    },
    groups: {
        search: (pattern) => searchGroups({ pattern }),
        getId: (g) => g.group_id,
        getLabel: (g) => g.name
    }
}

export default function ExportRecipientsModal({ show, showExams, selectedUsers, selectedExams, selectedGroups, onApply, onClose }) {
    const t = useSelector(selectTranslations)

    const [activeTab, setActiveTab] = useState('users')
    const [pattern, setPattern] = useState('')
    const [results, setResults] = useState([])
    const [users, setUsers] = useState([])
    const [exams, setExams] = useState([])
    const [groups, setGroups] = useState([])

    const SELECTED_STATE = { users: [users, setUsers], exams: [exams, setExams], groups: [groups, setGroups] }

    useEffect(() => {
        if (show) {
            setUsers(selectedUsers || [])
            setExams(selectedExams || [])
            setGroups(selectedGroups || [])
            setActiveTab(showExams ? 'exams' : 'users')
            setPattern('')
            setResults([])
        }
    }, [show])

    const runSearch = debounce((tab, value) => {
        if (!value) {
            setResults([])
            return
        }
        TABS_CONFIG[tab].search(value).then((r) => {
            setResults(r.success ? (r.data || []) : [])
        }).catch(() => toast.error(t.error.error_loading))
    }, 300)

    const handlePatternChange = (value) => {
        setPattern(value)
        runSearch(activeTab, value)
    }

    const handleTabChange = (tab) => {
        setActiveTab(tab)
        setPattern('')
        setResults([])
    }

    const isSelected = (tab, item) => {
        const [selected] = SELECTED_STATE[tab]
        return selected.some(s => TABS_CONFIG[tab].getId(s) === TABS_CONFIG[tab].getId(item))
    }

    const toggleItem = (tab, item) => {
        const [selected, setSelected] = SELECTED_STATE[tab]
        const id = TABS_CONFIG[tab].getId(item)
        if (selected.some(s => TABS_CONFIG[tab].getId(s) === id)) {
            setSelected(selected.filter(s => TABS_CONFIG[tab].getId(s) !== id))
        } else {
            setSelected([...selected, item])
        }
    }

    const removeItem = (tab, item) => {
        const [selected, setSelected] = SELECTED_STATE[tab]
        const id = TABS_CONFIG[tab].getId(item)
        setSelected(selected.filter(s => TABS_CONFIG[tab].getId(s) !== id))
    }

    const handleApply = () => {
        onApply({ users, exams: showExams ? exams : [], groups })
    }

    const renderTabContent = (tab) => {
        const [selected] = SELECTED_STATE[tab]
        return (
            <div className="mt-3">
                {selected.length > 0 ?
                    <div className="d-flex flex-wrap mb-3">
                        {selected.map(item => (
                            <span key={TABS_CONFIG[tab].getId(item)} className="question_bank_tag question_bank_tag_subject me-2 mb-2 d-flex align-items-center">
                                {TABS_CONFIG[tab].getLabel(item)}
                                <img src={close_img} alt="" className="ms-2 pointer" onClick={() => removeItem(tab, item)} />
                            </span>
                        ))}
                    </div>
                : null}
                <Form.Control
                    type="text"
                    placeholder={t.exports.recipients_search_placeholder}
                    value={pattern}
                    onChange={(e) => handlePatternChange(e.target.value)}
                />
                <div className="table-responsive mt-2" style={{ maxHeight: '320px', overflowY: 'auto' }}>
                    <table className="table">
                        <tbody>
                            {results?.length ? results.map((item) => {
                                const checked = isSelected(tab, item)
                                return (
                                    <tr key={TABS_CONFIG[tab].getId(item)} style={{ cursor: 'pointer' }} onClick={() => toggleItem(tab, item)}>
                                        <td style={{ width: 40 }}>
                                            <input
                                                type="checkbox"
                                                className="input-check"
                                                checked={checked}
                                                onChange={() => toggleItem(tab, item)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </td>
                                        <td>{TABS_CONFIG[tab].getLabel(item)}</td>
                                    </tr>
                                )
                            }) : (
                                <tr>
                                    <td className="text-center text-muted">
                                        {pattern ? t.exports.no_results : t.exports.recipients_search_hint}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        )
    }

    return (
        <Modal show={show} onHide={onClose} centered size="lg">
            <Modal.Body>
                <h2 className="text-center">{t.exports.select_recipients}</h2>
                <Tabs activeKey={activeTab} onSelect={handleTabChange} id="export_recipients_tabs" className="mt-3 rounded_tabs">
                    <Tab eventKey="users" title={`${t.permissions.single_students} (${users.length})`}>
                        {activeTab === 'users' ? renderTabContent('users') : null}
                    </Tab>
                    {showExams ?
                        <Tab eventKey="exams" title={`${t.exports.level_exam} (${exams.length})`}>
                            {activeTab === 'exams' ? renderTabContent('exams') : null}
                        </Tab>
                    : null}
                    <Tab eventKey="groups" title={`${t.permissions.groups} (${groups.length})`}>
                        {activeTab === 'groups' ? renderTabContent('groups') : null}
                    </Tab>
                </Tabs>

                <div className="d-flex justify-content-end mt-3">
                    <button type="button" className="secondary me-2" onClick={onClose}>{t.modals.abort}</button>
                    <button type="button" onClick={handleApply}>{t.exports.apply_selection}</button>
                </div>
            </Modal.Body>
        </Modal>
    )
}
